import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import Pilot from "../../model/Pilot";
import PagedData from "../../model/PagedData";
import APIEndPoints from "../../utils/APIEndPoints";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import Loading from "../common/Loading";
import AppIcons from "../../utils/AppIcons";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import AppSubSections from "../../utils/AppSubSections";
import UserRoles from "../../model/enums/UserRole";
import PilotsListElement from "../pilots/PilotsListElement";
import _3DInstance from "../../model/_3DInstance";
import _3dModelsListElement from "../_3d_instances/_3dInstancesListElement";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";

/*
  Component to manage the display of the existing PILOTS list
*/
interface _3dInstanceSearchListProps {
  onSelect: (id: number) => void; // Accept undefined to handle safely
  parentID: number;
  searchText: string;
  selectedId: number | null;
}

function _3dInstanceSearchList({
  onSelect,
  parentID,
  searchText,
  selectedId,
}: _3dInstanceSearchListProps) {
  const authManager = useAuth();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [data, setData] = useState<_3DInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    setLoading(true);
    apiClient
      ?.get<PagedData<_3DInstance[]>>({
        url: `${APIEndPoints._3DINSTANCES.ALLFROMDB}?search=${searchText}&search_field=name`,
        requiresToken: true,
      })
      .then((data: PagedData<_3DInstance[]>) => {
        // Filter the data to exclude the item with the specified ID
        const filteredItems = data.items
          .filter((item) => item.id !== parentID)
          .slice(0, 3);
        // setData(filteredItems);
        setData(filteredItems);
        setShowMore(data.items.length > 4);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [apiClient, searchText]);

  const handleShowMore = () => {
    apiClient
      ?.get<PagedData<_3DInstance[]>>({
        url: `${APIEndPoints._3DINSTANCES.ALLFROMDB}?search=${searchText}&search_field=name`,
        requiresToken: true,
      })
      .then((response: PagedData<_3DInstance[]>) => {
        setData(response.items);
        setShowMore(false);
      })
      .catch(() => {
        navigate(SETTINGS.get_path({ section: AppSections.SIGN_IN }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div>
      {data?.length === 0 && (
        <div className="p-4 border border-solid border-gray-200 rounded-xl">
          No records found. Change search parameters and try again.
        </div>
      )}
      <div className="grid grid-cols-3 gap-6">
        {data?.length > 0 &&
          data.map((item) => {
            const canEdit =
              !EmbeddingToolInfo.isChemiTool() &&
              (item.id_user === authManager.getUserID() ||
                authManager.getUserRole() === UserRoles.ADMIN);

            const isSelected = item.id === selectedId;

            return (
              <div
                // key={item.id ?? Math.random()} // Fallback key if id is undefined
                onClick={() => {
                  if (item.id !== undefined) {
                    onSelect(item.id); // Only call onSelect if id is defined
                  } else {
                    console.warn("Item ID is undefined, ignoring selection.");
                  }
                }}
                className={`p-4 w-full border border-solid border-gray-200 rounded-xl mt-3 mb-5 text-gray-600 hover:bg-blue-50 cursor-pointer ${
                  isSelected ? "bg-blue-100" : ""
                }`}
              >
                <_3dModelsListElement
                  data={item}
                  canEdit={canEdit}
                  navigable={false}
                />
              </div>
            );
          })}

        {showMore && (
          <div className="details-show-more">
            <Button
              label="Show all"
              onClick={handleShowMore}
              purpose={ButtonAction.UPDATE}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default _3dInstanceSearchList;
